import { FetchTimeoutError } from '@/services/http-service';

export const DEFAULT_MAX_RETRIES = 3;
export const DEFAULT_BASE_DELAY_MS = 250;
export const DEFAULT_MAX_DELAY_MS = 4000;

const IDEMPOTENT_METHODS = new Set(['GET', 'HEAD', 'OPTIONS', 'PUT', 'DELETE']);

export type RetryReason = 'server' | 'network' | 'timeout';

export interface RetryAttempt {
	attempt: number;
	maxAttempts: number;
	reason: RetryReason;
	status?: number;
	delayMs: number;
	path: string;
}

// Listener invoked before sleeping for the next attempt. Used to surface
// "retrying..." toasts to the user without coupling api-service to React.
export type RetryListener = (event: RetryAttempt) => void;

export interface ResolveEvent {
	path: string;
	succeededOnAttempt: number;
}
export type RetryResolveListener = (event: ResolveEvent) => void;

export interface FailEvent {
	path: string;
	attempts: number;
	status?: number;
}
export type RetryFailListener = (event: FailEvent) => void;

export const normalizeMethod = (method: string | undefined): string => {
	return (method || 'GET').toUpperCase();
};

export const isIdempotentMethod = (method: string | undefined): boolean => {
	return IDEMPOTENT_METHODS.has(normalizeMethod(method));
};

// Network failures (no response received) — fetch throws a TypeError or
// timeout/abort error. These are safe to retry for ANY method because the
// server may not have received the request.
export const isNetworkRetryable = (error: unknown): boolean => {
	if (error instanceof FetchTimeoutError) {
		return true;
	}
	if (!error || typeof error !== 'object') {
		return false;
	}
	const name = (error as { name?: string }).name?.toLowerCase() ?? '';
	if (name === 'aborterror' || name === 'timeouterror') {
		return true;
	}
	const message = error instanceof Error ? error.message.toLowerCase() : '';
	return (
		message.includes('network request failed')
		|| message.includes('failed to fetch')
		|| message.includes('load failed')
		|| message.includes('timed out')
		|| message.includes('timeout')
	);
};

// Compute exponential backoff with full jitter, optionally honoring a
// server-supplied Retry-After value (seconds or HTTP-date).
export const computeBackoffMs = (
	attempt: number,
	options: { baseMs?: number; maxMs?: number; retryAfter?: string | null } = {},
): number => {
	const base = options.baseMs ?? DEFAULT_BASE_DELAY_MS;
	const max = options.maxMs ?? DEFAULT_MAX_DELAY_MS;

	if (options.retryAfter) {
		const retryAfterMs = parseRetryAfter(options.retryAfter);
		if (retryAfterMs !== null) {
			return Math.min(retryAfterMs, max);
		}
	}

	const exp = Math.min(max, base * Math.pow(2, attempt - 1));
	// Full jitter — pick a random value in [0, exp]. Smooths out
	// retry storms when many clients fail at once.
	return Math.floor(Math.random() * exp);
};

const parseRetryAfter = (header: string): number | null => {
	const seconds = Number(header);
	if (Number.isFinite(seconds) && seconds >= 0) {
		return Math.floor(seconds * 1000);
	}
	const date = Date.parse(header);
	if (Number.isFinite(date)) {
		return Math.max(0, date - Date.now());
	}
	return null;
};

export const sleep = (ms: number): Promise<void> => {
	if (ms <= 0) return Promise.resolve();
	return new Promise((resolve) => setTimeout(resolve, ms));
};
